import {
  refinementList,
  rangeSlider,
  rangeInput,
  toggleRefinement,
  menu,
  numericMenu,
  searchBox,
  clearRefinements,
  stats,
  sortBy,
} from 'instantsearch.js/es/widgets';

import {
  connectNumericMenu,
  connectMenu,
} from 'instantsearch.js/es/connectors';

import { createPanel } from './createPanel.js';
import {
  itemTemplate,
  showMoreTemplate,
  commonClasses,
  statsTemplate,
  paginationCountTemplate,
  clearRefinementsTemplate,
  rangeInputTemplate,
  texts,
} from './common.js';
import { algoliaLog } from '../config/algolia.js';
import { createFilteredQueryHook } from './queryFilter.js';

/**
 * Predefined transform functions for refinement lists
 */
const transformFunctions = {
  productTypeLabels: (items) =>
    items.map((item) => ({
      ...item,
      label:
        {
          robot: 'Robot',
          robodrill: 'ROBODRILL',
          cnc: 'CNC',
        }[item.label] || item.label,
    })),
  accessoryTypeLabels: (items) =>
    items.map((item) => ({
      ...item,
      label:
        {
          hardware: 'Hardware',
          rtu: 'RTU',
          software: 'Software',
        }[item.label] || item.label,
    })),
};

/**
 * Widget configurations with Tailwind classes
 */
const widgetConfigs = {
  refinementList: (container, attribute, options) => {
    const usePanel = container.dataset.panel !== 'false';
    const label = container.dataset.label || attribute;
    const transformItemsKey = container.dataset.transformItems;

    const widgetOptions = {
      templates: {
        item: itemTemplate,
        showMoreText: showMoreTemplate,
      },
      cssClasses: {
        root: '',
        list: commonClasses.list.join(' '),
        item: commonClasses.item.join(' '),
        selectedItem: commonClasses.selectedItem.join(' '),
        showMore: commonClasses.showMore.join(' '),
        noRefinementRoot: commonClasses.noRefinementRoot.join(' '),
        searchBox: 'mb-3',
      },
      ...options,
    };

    // Add transformItems if specified
    if (transformItemsKey && transformFunctions[transformItemsKey]) {
      widgetOptions.transformItems = transformFunctions[transformItemsKey];
    }

    if (usePanel) {
      return createPanel(refinementList, {
        container,
        attribute,
        label,
        widgetOptions,
      });
    }

    return refinementList({
      container,
      attribute,
      ...widgetOptions,
    });
  },

  rangeSlider: (container, attribute, options) => {
    const usePanel = container.dataset.panel !== 'false';
    const label = container.dataset.label || attribute;

    const widgetOptions = {
      cssClasses: {
        root: 'py-4',
        form: '',
      },
      ...options,
    };

    if (usePanel) {
      return createPanel(rangeSlider, {
        container,
        attribute,
        label,
        widgetOptions,
      });
    }

    return rangeSlider({
      container,
      attribute,
      ...widgetOptions,
    });
  },

  rangeInput: (container, attribute, options) => {
    const usePanel = container.dataset.panel !== 'false';
    const label = container.dataset.label || attribute;

    const widgetOptions = {
      templates: rangeInputTemplate,
      cssClasses: {
        root: '',
        form: 'flex items-center',
        input: 'input-text w-20',
        separator: '',
        submit: '',
      },
      ...options,
    };

    if (usePanel) {
      return createPanel(rangeInput, {
        container,
        attribute,
        label,
        widgetOptions,
      });
    }

    return rangeInput({
      container,
      attribute,
      ...widgetOptions,
    });
  },

  toggleRefinement: (container, attribute, options) => {
    return toggleRefinement({
      container,
      attribute,
      templates: {
        labelText: () => container.dataset.label || attribute,
      },
      cssClasses: {
        root: 'py-1',
        label: 'flex items-center gap-2 cursor-pointer text-sm text-gray-700',
        checkbox: 'input-check',
      },
      ...options,
    });
  },

  menu: (container, attribute, options) => {
    const usePanel = container.dataset.panel !== 'false';
    const label = container.dataset.label || attribute;
    const isInline = container.dataset.inline === 'true';
    const showAll = container.dataset.showAll === 'true';
    const allLabel = container.dataset.allLabel || texts.all;
    const transformItemsKey = container.dataset.transformItems;

    // Inline mode - horizontal tabs with custom connector
    if (isInline) {
      let cachedWrapper = null;
      let currentRefine = null;

      const renderMenuTabs = (renderOptions, isFirstRender) => {
        const { items, refine } = renderOptions;
        currentRefine = refine;
        const containerEl =
          typeof container === 'string'
            ? document.querySelector(container)
            : container;

        if (isFirstRender) {
          const wrapper = document.createElement('div');
          wrapper.className = 'filter-tabs';
          // Event delegation - single listener for all buttons (prevents memory leak)
          wrapper.addEventListener('click', (e) => {
            const btn = e.target.closest('button');
            if (btn && btn.dataset.value !== undefined && currentRefine) {
              currentRefine(btn.dataset.value);
            }
          });
          containerEl.appendChild(wrapper);
          cachedWrapper = wrapper;
        }

        const wrapper = cachedWrapper || containerEl.querySelector('div');
        if (!wrapper) return;

        // Check if any item is refined
        const hasRefinement = items.some((item) => item.isRefined);

        // Build tabs HTML using CSS classes from filters.css
        let html = '';

        // Add "All" option if showAll is enabled
        if (showAll) {
          const isAllActive = !hasRefinement;
          html += `
            <button type="button" data-value="" class="filter-tab ${isAllActive ? 'filter-tab--active' : ''}">
              ${allLabel}
            </button>
          `;
        }

        // Add item tabs
        items.forEach((item) => {
          html += `
            <button type="button" data-value="${item.value}" class="filter-tab js-apply-italic-i ${item.isRefined ? 'filter-tab--active' : ''}">
              ${item.label}
            </button>
          `;
        });

        wrapper.innerHTML = html;
      };

      // Build connector options with transformItems
      const connectorOptions = { attribute, ...options };
      if (transformItemsKey && transformFunctions[transformItemsKey]) {
        connectorOptions.transformItems = transformFunctions[transformItemsKey];
      }

      return connectMenu(renderMenuTabs)(connectorOptions);
    }

    // Standard menu mode
    const widgetOptions = {
      cssClasses: {
        root: '',
        list: 'space-y-1',
        item: 'py-1',
        selectedItem: 'font-medium',
        link: 'text-sm text-gray-700 hover:text-gray-900',
        count: 'ml-2 text-xs text-gray-400',
      },
      ...options,
    };

    // Add transformItems if specified
    if (transformItemsKey && transformFunctions[transformItemsKey]) {
      widgetOptions.transformItems = transformFunctions[transformItemsKey];
    }

    if (usePanel) {
      return createPanel(menu, {
        container,
        attribute,
        label,
        widgetOptions,
      });
    }

    return menu({
      container,
      attribute,
      ...widgetOptions,
    });
  },

  menuSelect: (container, attribute, options) => {
    const allLabel = container.dataset.allLabel || texts.all;
    let cachedSelect = null;
    let currentRefine = null;

    const renderMenuSelect = (renderOptions, isFirstRender) => {
      const { items, refine } = renderOptions;
      currentRefine = refine;
      const containerEl =
        typeof container === 'string'
          ? document.querySelector(container)
          : container;

      if (isFirstRender) {
        const select = document.createElement('select');
        select.className = 'input-select';
        select.addEventListener('change', (e) => {
          if (currentRefine) {
            currentRefine(e.target.value);
          }
        });
        containerEl.appendChild(select);
        cachedSelect = select;
      }

      const select = cachedSelect || containerEl.querySelector('select');
      if (!select) return;

      // Build options HTML with "All" option first
      let html = `<option value="">${allLabel}</option>`;
      items.forEach((item) => {
        html += `<option value="${item.value}" ${item.isRefined ? 'selected' : ''}>${item.label}</option>`;
      });

      select.innerHTML = html;
    };

    return connectMenu(renderMenuSelect)({
      attribute,
      ...options,
    });
  },

  numericMenu: (container, attribute, options) => {
    const usePanel = container.dataset.panel !== 'false';
    const label = container.dataset.label || attribute;
    const display = container.dataset.display || 'radio'; // 'radio' or 'dropdown'

    // Dropdown display mode - uses connector for native <select>
    if (display === 'dropdown') {
      const renderNumericMenuDropdown = (renderOptions, isFirstRender) => {
        const { items, refine } = renderOptions;
        const containerEl =
          typeof container === 'string'
            ? document.querySelector(container)
            : container;

        if (isFirstRender) {
          // Create wrapper structure
          const wrapper = document.createElement('div');
          wrapper.className = 'mb-5';

          const labelEl = document.createElement('label');
          labelEl.className =
            'block text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3';
          labelEl.textContent = label;

          const select = document.createElement('select');
          select.className = 'input-select w-full';
          select.addEventListener('change', (e) => {
            refine(e.target.value);
          });

          wrapper.appendChild(labelEl);
          wrapper.appendChild(select);
          containerEl.appendChild(wrapper);
        }

        // Update select options
        const select = containerEl.querySelector('select');
        if (select) {
          select.innerHTML = items
            .map(
              (item) =>
                `<option value="${item.value}" ${item.isRefined ? 'selected' : ''}>${item.label}</option>`,
            )
            .join('');
        }
      };

      return connectNumericMenu(renderNumericMenuDropdown)({
        attribute,
        ...options,
      });
    }

    // Radio button display mode (default)
    const widgetOptions = {
      templates: {
        item: (item, { html }) => html`
          <label
            class="flex items-center gap-2 cursor-pointer text-sm text-gray-700 py-1.5"
          >
            <input
              type="radio"
              name="${attribute}"
              class="input-radio"
              checked=${item.isRefined}
            />
            <span class="${item.isRefined ? 'font-medium text-gray-900' : ''}"
              >${item.label}</span
            >
          </label>
        `,
      },
      cssClasses: {
        root: '',
        list: 'space-y-0.5',
        item: '',
        selectedItem: '',
        label: '',
        radio: '',
      },
      ...options,
    };

    if (usePanel) {
      return createPanel(numericMenu, {
        container,
        attribute,
        label,
        widgetOptions,
      });
    }

    return numericMenu({
      container,
      attribute,
      ...widgetOptions,
    });
  },

  searchBox: (container, _, options) => {
    const debounceMs = parseInt(container.dataset.debounce, 10) || 350;
    const minLength = parseInt(container.dataset.minLength, 10) || 2;

    return searchBox({
      container,
      placeholder: container.dataset.placeholder || texts.searchPlaceholder,
      queryHook: createFilteredQueryHook(debounceMs, minLength),
      cssClasses: {
        root: '',
        form: 'relative',
        input: 'input-text',
        submit: 'absolute right-3 top-1/2 -translate-y-1/2 text-gray-400',
        reset:
          'absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600',
        loadingIndicator: 'absolute right-3 top-1/2 -translate-y-1/2',
      },
      templates: {
        submit: `
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        `,
        reset: `
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        `,
      },
      ...options,
    });
  },

  clearRefinements: (container, _, options) => {
    if (container.dataset.excludedAttributes) {
      try {
        options.excludedAttributes = JSON.parse(
          container.dataset.excludedAttributes,
        );
      } catch {
        // fallback: split comma-separated string
        options.excludedAttributes =
          container.dataset.excludedAttributes.split(',');
      }
    }

    return clearRefinements({
      container,
      templates: clearRefinementsTemplate,
      cssClasses: {
        root: '',
        button: 'filter-clear',
        disabledButton: 'hidden',
      },
      ...options,
    });
  },

  stats: (container, _, options) => {
    return stats({
      container,
      templates: {
        text: statsTemplate,
      },
      cssClasses: {
        root: '',
        text: '',
      },
      ...options,
    });
  },

  paginationCount: (container, _, options) => {
    const label = container.dataset.label || '';
    return stats({
      container,
      templates: {
        text: paginationCountTemplate(label),
      },
      cssClasses: {
        root: '',
        text: '',
      },
      ...options,
    });
  },

  sortBy: (container, _, options) => {
    const widget = sortBy({
      container,
      cssClasses: {
        root: '',
        select: 'input-select sort-select',
      },
      ...options,
    });

    return widget;
  },
};

/**
 * Create widget instances from DOM elements with data-widget-auto attribute
 * @param {HTMLElement[]} containers - Array of container elements
 * @returns {Array} Array of widget instances
 */
export function createWidgetInstances(containers) {
  algoliaLog(
    '[createWidgetInstances] Processing containers:',
    containers.length,
  );
  return containers
    .map((container) => {
      const widgetType = container.dataset.widgetType;
      const attribute = container.dataset.attribute;

      algoliaLog('[createWidgetInstances] Widget:', {
        widgetType,
        attribute,
      });

      const widgetFactory = widgetConfigs[widgetType];

      if (!widgetFactory) {
        console.warn(`Unknown widget type: ${widgetType}`);
        return null;
      }

      const options = parseWidgetOptions(container);
      return widgetFactory(container, attribute, options);
    })
    .filter(Boolean);
}

/**
 * Parse widget options from data attributes
 * @param {HTMLElement} container - Widget container element
 * @returns {Object} Widget options
 */
function parseWidgetOptions(container) {
  const options = {};

  // Common options
  if (container.dataset.limit) {
    options.limit = parseInt(container.dataset.limit, 10);
  }

  if (container.dataset.showMore === 'true') {
    options.showMore = true;
    options.showMoreLimit = parseInt(container.dataset.showMoreLimit, 10) || 20;
  }

  if (container.dataset.searchable === 'true') {
    options.searchable = true;
    options.searchablePlaceholder =
      container.dataset.searchablePlaceholder || texts.searchPlaceholder;
  }

  if (container.dataset.sortBy) {
    options.sortBy = container.dataset.sortBy.split(',');
  }

  // Range widget options
  if (container.dataset.min) {
    options.min = parseInt(container.dataset.min, 10);
  }

  if (container.dataset.max) {
    options.max = parseInt(container.dataset.max, 10);
  }

  if (container.dataset.precision) {
    options.precision = parseInt(container.dataset.precision, 10);
  }

  // Toggle refinement options
  if (container.dataset.on) {
    options.on =
      container.dataset.on === 'true'
        ? true
        : container.dataset.on === 'false'
          ? false
          : container.dataset.on;
  }

  if (container.dataset.off) {
    options.off =
      container.dataset.off === 'true'
        ? true
        : container.dataset.off === 'false'
          ? false
          : container.dataset.off;
  }

  // Sort by items
  if (container.dataset.items) {
    try {
      options.items = JSON.parse(container.dataset.items);
    } catch (e) {
      console.warn('Error parsing sortBy items:', e);
    }
  }

  return options;
}
